import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  CanActivateChild,
  CanLoad,
  Route,
  Router,
  UrlSegment,
} from '@angular/router';
import { catchError, map, Observable, of, Subject, switchMap, tap } from 'rxjs';
import { AuthenticatedUserService } from 'src/app/core/services/authenticated-user/authenticated-user.service';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import { RoutesKeys } from 'src/app/utils/routes';
import { setTranslocoLang } from 'src/app/utils/translocoLang';
import { AdminStatus } from '../../models/constants/adminStatus';
import { UserService } from 'src/app/core/api/user/user.service';

@Injectable({
  providedIn: 'root',
})
/**
 * User status guard will verify if the user had complete registration before proceeding to the next page
 */
export class UserGuard implements CanActivate, CanActivateChild, CanLoad {
  public loader$ = new Subject<boolean>();

  /**
   * Injects angular router
   * @constructor
   */
  constructor(
    private router: Router,
    private translationService: TranslationService,
    private readonly authenticatedUserService: AuthenticatedUserService,
    private readonly userService: UserService
  ) {}

  /**
   * @param {Route} _ unused
   * @param {UrlSegment[]} segments
   * @return {Observable<boolean>}
   */
  canLoad(_: Route, segments: UrlSegment[]): Observable<boolean> {
    return this.canActivate({ url: segments } as ActivatedRouteSnapshot);
  }

  /**
   * Method that check if the user had complete registration
   * @param {ActivatedRouteSnapshot} route
   * @return {Observable<boolean>}
   */
  canActivateChild(route: ActivatedRouteSnapshot): Observable<boolean> {
    return this.canActivate(route);
  }
  /**
   * Method that check if the user had complete registration
   * @param {ActivatedRouteSnapshot} route
   * @return {Observable<boolean>}
   */
  canActivate(route: ActivatedRouteSnapshot): Observable<boolean> {
    this.loader$.next(true);

    return this.authenticatedUserService.initializeAdmin().pipe(
      catchError((err) => {
        this.loader$.next(false);
        // TODO: go to service unavailable page
        throw err;
      }),
      switchMap(({ admin, status }) => {
        if (!admin) {
          this.router.navigate([RoutesKeys.HOME]);
          return of(false);
        }

        if (
          route.data &&
          Object.prototype.hasOwnProperty.call(route.data, 'roles')
        ) {
          const allowedRoles = route.data['roles'] as string[];
          if (!allowedRoles.some((role) => admin.role.includes(role))) {
            this.router.navigate([RoutesKeys.HOME]);
          }
        }

        const translocoLang = setTranslocoLang(admin.language);
        this.translationService.setActiveLang(translocoLang);
        if (
          route.url[0]?.path !== RoutesKeys.ONBOARDING &&
          status === AdminStatus.INCOMPLETE
        ) {
          this.router.navigate([RoutesKeys.ONBOARDING]);
          return of(false);
        } else if (
          route.url[0]?.path === RoutesKeys.ONBOARDING &&
          status !== AdminStatus.INCOMPLETE
        ) {
          this.router.navigate([RoutesKeys.HOME]);
          return of(false);
        }
        return of(true);
      }),
      switchMap((initialResult) => {
        if (!initialResult) {
          this.loader$.next(false);
          return of(false);
        }

        if (
          route.url[0]?.path === RoutesKeys.CUSTOMER_DETAIL &&
          route.url[1]?.path
        ) {
          const customerId = +route.url[1].path;
          return this.userService.getBusinessSummary(customerId).pipe(
            map(() => true),
            catchError(() => {
              this.router.navigate([RoutesKeys.HOME]);
              return of(false);
            })
          );
        }
        this.loader$.next(false);
        return of(true);
      }),
      tap(() => {
        this.loader$.next(false);
      })
    );
  }
}
